/*============================================================================
  theme.js — Status Audio theme global JS

  Replaces the legacy function-timber.js + function-global.js pair.

  Public API (window.StatusTheme):
    - formatMoney(cents, format)
    - Drawer (class)
    - drawers.nav, drawers.cart  (instances, available after DOMContentLoaded)
    - productPage({ variant })

  Also assigns Shopify.formatMoney for back-compat with other scripts.
==============================================================================*/
(function () {
  'use strict'

  /*==========================================================================
    Utilities
  ==========================================================================*/
  function swingEase(progress) {
    return 0.5 - Math.cos(progress * Math.PI) / 2
  }

  function prepareTransition($el) {
    const events = ['transitionend', 'TransitionEnd', 'webkitTransitionEnd', 'oTransitionEnd']

    function done() {
      $el.classList.remove('is-transitioning')
    }

    events.forEach(function (name) {
      $el.addEventListener(name, done, { once: true })
    })

    const style = window.getComputedStyle($el)
    const props = [
      'transition-duration',
      '-moz-transition-duration',
      '-webkit-transition-duration',
      '-o-transition-duration',
    ]
    let duration = 0
    props.forEach(function (prop) {
      duration = parseFloat(style.getPropertyValue(prop)) || duration
    })

    if (duration !== 0) {
      $el.classList.add('is-transitioning')
      // Force reflow so the transition restarts cleanly.
      void $el.offsetWidth
    }
  }

  function slideElement($el, opening, duration) {
    const isHidden = window.getComputedStyle($el).display === 'none'
    if (opening && !isHidden) return
    if (!opening && isHidden) return

    $el.style.boxSizing = 'border-box'
    $el.style.overflow = 'hidden'

    if (opening) {
      $el.style.removeProperty('display')
      let display = window.getComputedStyle($el).display
      if (display === 'none') display = 'block'
      $el.style.display = display
    }

    const startHeight = opening ? 0 : $el.offsetHeight
    const endHeight = opening ? $el.scrollHeight : 0
    const startTime = performance.now()

    if (opening) $el.style.height = '0px'

    function step(now) {
      const progress = duration === 0 ? 1 : Math.min((now - startTime) / duration, 1)
      const eased = swingEase(progress)
      $el.style.height = `${startHeight + (endHeight - startHeight) * eased}px`

      if (progress < 1) {
        requestAnimationFrame(step)
      } else {
        $el.style.removeProperty('height')
        $el.style.removeProperty('overflow')
        $el.style.removeProperty('box-sizing')
        if (!opening) $el.style.display = 'none'
      }
    }

    requestAnimationFrame(step)
  }

  function slideDown($el, duration) {
    slideElement($el, true, duration == null ? 400 : duration)
  }

  function slideUp($el, duration) {
    slideElement($el, false, duration == null ? 400 : duration)
  }

  function slideToggle($el, duration) {
    const isHidden = window.getComputedStyle($el).display === 'none'
    if (isHidden) slideDown($el, duration)
    else slideUp($el, duration)
  }

  function animateScrollTo(targetTop, duration, onComplete) {
    const startTop = window.scrollY
    const distance = targetTop - startTop
    const startTime = performance.now()

    function step(now) {
      const progress = Math.min((now - startTime) / duration, 1)
      window.scrollTo(0, startTop + distance * swingEase(progress))

      if (progress < 1) {
        requestAnimationFrame(step)
      } else if (onComplete) {
        onComplete()
      }
    }

    requestAnimationFrame(step)
  }

  /*==========================================================================
    Money formatting

    Polyfills Shopify.formatMoney when not already defined and exposes the
    same function on StatusTheme.formatMoney.
  ==========================================================================*/
  function formatMoney(cents, format) {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
    const formatString = format || (window.Shopify && Shopify.money_format) || '${{amount}}'

    if (typeof cents === 'string') {
      cents = cents.replace('.', '')
    }

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2)
      thousands = defaultOption(thousands, ',')
      decimal = defaultOption(decimal, '.')

      if (isNaN(number) || number == null) return 0

      number = (number / 100.0).toFixed(precision)

      const parts = number.split('.')
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands)
      const centsPart = parts[1] ? decimal + parts[1] : ''

      return dollars + centsPart
    }

    let value = ''
    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2)
        break
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0)
        break
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',')
        break
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',')
        break
    }

    return formatString.replace(placeholderRegex, value)
  }

  if (typeof window.Shopify === 'undefined') window.Shopify = {}
  if (!Shopify.formatMoney) Shopify.formatMoney = formatMoney

  /*==========================================================================
    Drawer

    Drop-in replacement for the legacy timber.Drawers IIFE.

    DOM contract (unchanged):
      - drawer element id, e.g. #NavDrawer / #CartDrawer
      - openers: .js-drawer-open-<position>  (e.g. .js-drawer-open-left)
      - closers inside the drawer: .js-drawer-close
      - body / html toggles classes: js-drawer-open, js-drawer-open-<position>
      - elements that should transition with the drawer: .is-moved-by-drawer
      - the page container is #PageContainer
  ==========================================================================*/
  class Drawer {
    constructor(id, position, options) {
      this.$drawer = document.getElementById(id)
      if (!this.$drawer) return

      this.position = position
      this.options = Object.assign(
        {
          close: '.js-drawer-close',
          open: '.js-drawer-open-' + position,
          openClass: 'js-drawer-open',
          dirOpenClass: 'js-drawer-open-' + position,
          onOpen: null,
        },
        options || {}
      )

      this.$body = document.body
      this.$html = document.documentElement
      this.$page = document.getElementById('PageContainer')
      this.$$moved = document.querySelectorAll('.is-moved-by-drawer')
      this.$activeSource = null
      this.isOpen = false

      this._pageTouchHandler = null
      this._pageClickHandler = null
      this._focusTrapHandler = null

      this._bind()
    }

    _bind() {
      const self = this
      document.querySelectorAll(this.options.open).forEach(function ($trigger) {
        $trigger.addEventListener('click', function (evt) {
          self.open(evt)
        })
      })
      this.$drawer.querySelectorAll(this.options.close).forEach(function ($close) {
        $close.addEventListener('click', function (evt) {
          evt.preventDefault()
          self.close()
        })
      })
    }

    open(evt) {
      const $header = document.querySelector('#mainHeader .header')
      const $overlay = document.querySelector('.cart-overlay')

      if ($header) {
        this.$drawer.style.top = `${$header.offsetHeight}px`
        if ($overlay) $overlay.style.top = `${$header.offsetHeight}px`
      }

      let external = false
      if (evt) {
        evt.preventDefault()
        if (evt.stopPropagation) {
          evt.stopPropagation()
          this.$activeSource = evt.currentTarget
        }
      } else {
        external = true
      }

      if (this.isOpen && !external) return this.close()
      if (this.isOpen) return

      // Close the sibling drawer if it's open.
      if (StatusTheme.drawers) {
        const self = this
        Object.values(StatusTheme.drawers).forEach(function (other) {
          if (other && other !== self && typeof other.close === 'function') other.close()
        })
      }

      this.$$moved.forEach(function ($node) {
        $node.classList.add('is-transitioning')
      })
      prepareTransition(this.$drawer)

      this.$body.classList.add(this.options.openClass, this.options.dirOpenClass)
      this.$html.classList.add(this.options.openClass, this.options.dirOpenClass)
      this.isOpen = true

      this._trapFocus()

      if (!external && typeof this.options.onOpen === 'function') {
        this.options.onOpen()
      }

      if (this.$activeSource && this.$activeSource.hasAttribute('aria-expanded')) {
        this.$activeSource.setAttribute('aria-expanded', 'true')
      }

      if (this.$page) {
        const self = this
        this._unbindPage()
        this._pageTouchHandler = function (e) {
          e.preventDefault()
        }
        this._pageClickHandler = function () {
          self.close()
        }
        this.$page.addEventListener('touchmove', this._pageTouchHandler, { passive: false })
        this.$page.addEventListener('click', this._pageClickHandler)
      }
    }

    close() {
      if (!this.isOpen) return

      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur()
      }

      this.$$moved.forEach(function ($node) {
        prepareTransition($node)
      })
      prepareTransition(this.$drawer)

      this.$body.classList.remove(this.options.openClass, this.options.dirOpenClass)
      this.$html.classList.remove(this.options.openClass, this.options.dirOpenClass)
      this.isOpen = false

      this._releaseFocus()
      this._unbindPage()
    }

    _unbindPage() {
      if (!this.$page) return

      if (this._pageTouchHandler) {
        this.$page.removeEventListener('touchmove', this._pageTouchHandler)
        this._pageTouchHandler = null
      }
      if (this._pageClickHandler) {
        this.$page.removeEventListener('click', this._pageClickHandler)
        this._pageClickHandler = null
      }
    }

    _trapFocus() {
      this._releaseFocus()

      const $container = this.$drawer
      $container.setAttribute('tabindex', '-1')
      $container.focus()

      this._focusTrapHandler = function (evt) {
        if ($container !== evt.target && !$container.contains(evt.target)) {
          $container.focus()
        }
      }
      document.addEventListener('focusin', this._focusTrapHandler)
    }

    _releaseFocus() {
      this.$drawer.removeAttribute('tabindex')
      if (this._focusTrapHandler) {
        document.removeEventListener('focusin', this._focusTrapHandler)
        this._focusTrapHandler = null
      }
    }
  }

  /*==========================================================================
    Product page — variant state

    Updates the Add to Cart button label/disabled state and the quantity
    inputs based on the currently selected variant. Called from
    snippets/scripts-pdps.liquid via Shopify.OptionSelectors.
  ==========================================================================*/
  function productPage(options) {
    const variant = options && options.variant
    const $addToCart = document.getElementById('AddToCart')
    const $$qtyBtns = document.querySelectorAll('.js-qty__adjust')
    const $$qtyInputs = document.querySelectorAll('.js-qty__num')

    function setState(enabled, label, qty) {
      if ($addToCart) {
        $addToCart.classList.toggle('disabled', !enabled)
        $addToCart.disabled = !enabled
        $addToCart.textContent = label
      }
      $$qtyBtns.forEach(function ($btn) {
        $btn.classList.toggle('disabled', !enabled)
        $btn.disabled = !enabled
      })
      $$qtyInputs.forEach(function ($input) {
        $input.classList.toggle('disabled', !enabled)
        $input.disabled = !enabled
        $input.value = qty
      })
    }

    if (variant && variant.available) {
      const label = $addToCart && $addToCart.classList.contains('preorder') ? 'Preorder' : 'Add to cart'
      setState(true, label, 1)
    } else {
      setState(false, 'Sold out', 0)
    }
  }

  /*==========================================================================
    Public namespace
  ==========================================================================*/
  const StatusTheme = {
    formatMoney: formatMoney,
    Drawer: Drawer,
    drawers: {},
    productPage: productPage,
  }
  window.StatusTheme = StatusTheme

  /*==========================================================================
    Boot — DOMContentLoaded
  ==========================================================================*/
  function initDrawers() {
    StatusTheme.drawers.nav = new Drawer('NavDrawer', 'left')
    StatusTheme.drawers.cart = new Drawer('CartDrawer', 'right', {
      onOpen: function () {
        if (typeof ajaxCart !== 'undefined' && typeof ajaxCart.load === 'function') {
          ajaxCart.load()
        }
      },
    })
  }

  function initNavAccordion() {
    document.querySelectorAll('#NavDrawer .listmenu .dropMenu').forEach(function ($dropMenu) {
      $dropMenu.addEventListener('click', function () {
        $dropMenu.classList.toggle('change')
        const $subMenu = $dropMenu.nextElementSibling
        if ($subMenu && $subMenu.classList.contains('subMenu')) {
          slideToggle($subMenu, 300)
        }
      })
    })
  }

  function initAnnouncementBanner() {
    if (document.querySelector('header .specialMsg')) {
      document.body.classList.add('hasBanner')
    }
  }

  function initAjaxCart() {
    if (typeof ajaxCart === 'undefined') return
    ajaxCart.init({
      formSelector: '#AddToCartForm',
      cartContainer: '#CartContainer',
      addToCartSelector: '#AddToCart',
      cartCountSelector: '#CartCount',
      cartCostSelector: '#CartCost',
      moneyFormat: window.shopMoneyFormat,
    })
  }

  function initStickyHeader() {
    const $header = document.querySelector('header .header')
    if (!$header) return

    const headerOffset = $header.getBoundingClientRect().top + window.scrollY

    window.addEventListener(
      'scroll',
      function () {
        if (window.scrollY > headerOffset) {
          document.body.classList.add('stickyHeader')
          $header.classList.add('activeSticky')
        } else {
          document.body.classList.remove('stickyHeader')
          $header.classList.remove('activeSticky')
        }
      },
      { passive: true }
    )
  }

  function initShopDropdown() {
    const $$dropdowns = document.querySelectorAll('[data-shop-dropdown]')
    if (!$$dropdowns.length) return

    document.addEventListener('click', function (e) {
      $$dropdowns.forEach(function ($dropdown) {
        if (!$dropdown.contains(e.target)) $dropdown.removeAttribute('open')
      })
    })
  }

  function initCurrencyDropdown() {
    document.querySelectorAll('[data-currency-dropdown]').forEach(function ($root) {
      const $toggle = $root.querySelector('[data-toggle]')
      const $input = $root.querySelector('[data-country-input]')
      const $$items = Array.from($root.querySelectorAll('.currency-dropdown__item'))

      function open() {
        $root.classList.add('is-open')
        $toggle.setAttribute('aria-expanded', 'true')
        const $selected = $root.querySelector('.currency-dropdown__item.is-selected') || $$items[0]
        if ($selected) $selected.focus()
      }

      function close() {
        $root.classList.remove('is-open')
        $toggle.setAttribute('aria-expanded', 'false')
      }

      $toggle.addEventListener('click', function (e) {
        e.stopPropagation()
        $root.classList.contains('is-open') ? close() : open()
      })

      $$items.forEach(function ($item) {
        $item.setAttribute('tabindex', '-1')
        $item.addEventListener('click', function () {
          if ($item.getAttribute('data-disabled')) return
          $input.value = $item.getAttribute('data-country')
          $input.form.submit()
        })
        $item.addEventListener('keydown', function (e) {
          const idx = $$items.indexOf($item)
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            $item.click()
          } else if (e.key === 'ArrowDown') {
            e.preventDefault()
            ;($$items[idx + 1] || $$items[0]).focus()
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            ;($$items[idx - 1] || $$items[$$items.length - 1]).focus()
          } else if (e.key === 'Escape') {
            close()
            $toggle.focus()
          }
        })
      })

      document.addEventListener('click', function (e) {
        if (!$root.contains(e.target)) close()
      })
    })
  }

  function initSmoothScroll() {
    document.querySelectorAll('a[href*="#"]').forEach(function ($link) {
      const href = $link.getAttribute('href')
      if (!href || href === '#' || href === '#0') return
      if ($link.classList.contains('accordion-section-title')) return
      if ($link.classList.contains('glightbox')) return

      $link.addEventListener('click', function (event) {
        const hash = $link.hash
        if (!hash) return

        const $target = document.querySelector(hash)
        if (!$target) return

        const $scrollHeader = document.querySelector('header .header')
        const headerHeight = $scrollHeader ? $scrollHeader.offsetHeight : 0
        const targetTop = $target.getBoundingClientRect().top + window.scrollY - headerHeight

        animateScrollTo(targetTop, 400, function () {
          if (history.pushState) {
            history.pushState(null, null, hash)
          } else {
            window.location.hash = hash
          }
        })

        event.preventDefault()
      })
    })
  }

  function initMegaMenus() {
    document.querySelectorAll('.megaMenu').forEach(function ($menuItem) {
      $menuItem.addEventListener('mouseenter', function () {
        const $megaDrop = $menuItem.querySelector('.megaDrop')
        $menuItem.classList.add('open')
        if ($megaDrop) {
          $megaDrop.style.display = 'block'
          setTimeout(function () {
            $megaDrop.style.opacity = '1'
          }, 50)
        }
      })
      $menuItem.addEventListener('mouseleave', function () {
        const $megaDrop = $menuItem.querySelector('.megaDrop')
        $menuItem.classList.remove('open')
        if ($megaDrop) {
          $megaDrop.style.opacity = '0'
          $menuItem.style.pointerEvents = 'none'
          setTimeout(function () {
            $megaDrop.style.display = 'none'
            $menuItem.style.pointerEvents = 'auto'
          }, 300)
        }
      })
    })
  }

  /*--------------------------------------------------------------------------
    Upsells — modal + drawer cart click handlers.
    Lives in global because the drawer cart can render on any page.
  --------------------------------------------------------------------------*/
  function initUpsells() {
    if (!window.upsellsEnabled) return

    function openModal(modalId) {
      const $modal = document.querySelector(`.upsell-modal[data-modal-id="${modalId}"]`)
      if ($modal) $modal.classList.toggle('show-modal')
    }

    function closeAllModals() {
      document.querySelectorAll('.upsell-modal').forEach(function ($modal) {
        $modal.classList.remove('show-modal')
      })
    }

    document.addEventListener('click', function (event) {
      const $learnMore = event.target.closest('.learn-more')
      const $modalClose = event.target.closest('.modal-close')
      const $addonBtn = event.target.closest('.js-addon-atc-btn')
      if (!$learnMore && !$modalClose && !$addonBtn) return

      if ($learnMore) openModal($learnMore.dataset.modalId)
      if ($modalClose) closeAllModals()

      if ($addonBtn) {
        event.preventDefault()

        const $addon = $addonBtn.closest('.addon')
        let upsellVariantId = $addonBtn.dataset.upsellVariantId

        if ($addon) {
          const $selectedVariant = $addon.querySelector('input.swatch__input:checked')
          if ($selectedVariant?.value) {
            upsellVariantId = $selectedVariant.value
          }
        }

        if (typeof ajaxCart !== 'undefined' && typeof ajaxCart.quickAdd === 'function') {
          ajaxCart.quickAdd(
            upsellVariantId,
            $addonBtn.dataset.parentVariantId,
            $addonBtn.dataset.parentProductId,
            $addonBtn.dataset.addOnDiscount,
            $addonBtn.dataset.sellingPlan
          )
        } else {
          console.warn('ajaxCart is not available; skipping upsell quick add.')
        }
      }
    })

    // Click outside the modal inner / learn-more trigger closes any open modal.
    window.addEventListener('click', function (event) {
      if (!event.target.closest('.modal-inner') && !event.target.closest('.learn-more')) {
        closeAllModals()
      }
    })
  }

  /*==========================================================================
    Wire up everything on DOM ready
  ==========================================================================*/
  document.addEventListener('DOMContentLoaded', function () {
    initDrawers()
    initNavAccordion()
    initAnnouncementBanner()
    initAjaxCart()
    initStickyHeader()
    initShopDropdown()
    initCurrencyDropdown()
    initSmoothScroll()
    initMegaMenus()
    initUpsells()
  })

  window.addEventListener('load', function () {
    document.body.classList.add('loaded')
  })

  document.body.addEventListener('ajaxCart.afterCartLoad', function (event) {
    if (!event.detail?.openDrawer) return
    if (StatusTheme.drawers.cart) StatusTheme.drawers.cart.open()
  })
})()
